import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import { DataTable, Button, Select, formatSelectOptions } from '../../../components'
import { useMessage, useModalDialog } from '../../../hooks'
import { personService } from '../../../services'
import { useNetworkDialog } from '../hooks/useNetworkDialog'
import { relationTypeClass } from '../helpers'

// Groups multi-select-only actions behind one button, mirroring recruitment's
// CandidateList.js — see the root CLAUDE.md's DataTableToolbar note: a bulk
// toolbar button calls `button.onClick()` with NO arguments, so any handler
// expecting `items` (like "Redigera") silently breaks the moment ≥1 row is
// selected. "Redigera"/"Ta bort" stay wired to `action:'edit'`/`'delete'` +
// `hideInToolbar:true` (per-row pencil/trash icons only); genuine bulk-only
// actions live here instead, invoked with the real `selectedItems`.
const BulkActionsMenu = ({ items, onExport, onDelete }) => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef()

  useEffect(() => {
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const run = (fn) => { setOpen(false); fn(items) }

  return (
    <div className="network-actions-menu-wrap" ref={wrapRef}>
      <Button value={`Åtgärder (${items.length}) ▾`} className="main-toolbar-item button" onClick={() => setOpen(o => !o)} />
      {open &&
        <div className="network-actions-menu">
          <button type="button" onClick={() => run(onExport)}>Exportera (CSV)</button>
          <button type="button" className="network-actions-menu-danger" onClick={() => run(onDelete)}>Ta bort</button>
        </div>
      }
    </div>
  )
}

const flatten = (p) => ({
  ...p,
  pills: [
    ...(p.relations || []).map(r => ({ label: r.relation_type_name, className: relationTypeClass[r.relation_type_code] || 'pill' })),
    ...(p.derived_relations || []).map(r => ({ label: r.relation_type_name, className: (relationTypeClass[r.relation_type_code] || 'pill') + ' pill-dashed' })),
  ],
  // Flattened for the table cell and the free-text filter. useFilterableData only ever
  // matches scalar string/boolean fields — it calls key.toLowerCase(), which silently
  // does nothing on the kayenta_contacts array — so the names have to exist as a plain
  // string for typing a colleague's name into the search box to work at all.
  kayenta_contact_names: (p.kayenta_contacts || []).map(c => c.user_fullname).join(', '),
})

// Same hand-rolled multi-select pattern as NetworkingOverview.js (no shared FilterBar
// component exists yet). The "Kayenta-kontakt" options deliberately come from the user
// list rather than being derived from the loaded rows: a colleague with zero connections
// must still be selectable, otherwise "0 träffar" can never be a real answer.
const MULTI_FILTER_KEYS = ['kayentaContact']
const EMPTY_FILTERS = { kayentaContact: [], missingContact: false }

const applyPersonFilters = (persons, filters) => persons.filter(p => {
  const contacts = p.kayenta_contacts || []
  if (filters.missingContact && contacts.length) return false
  // Union, not intersection — picking two colleagues answers "anyone connected to either".
  if (filters.kayentaContact.length &&
      !filters.kayentaContact.some(f => contacts.some(c => String(c.user_id) === String(f.value)))) return false
  return true
})

const INTERNAL_EXTERNAL_VALUES = {
  true: { label: 'Intern', className: 'pill' },
  false: { label: 'Extern', className: 'pill pill-grey' },
}

const PersonList = () => {

  const appName = useOutletContext()
  const navigate = useNavigate()
  const { showMessage } = useMessage()
  const { hideModalDialog } = useModalDialog()
  const { showPersonDialog, showBulkImportDialog, showDeletePersonDialog } = useNetworkDialog()

  const [persons, setPersons] = useState([])
  const [users, setUsers] = useState([])
  const [selectedItems, setSelectedItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState(EMPTY_FILTERS)

  const fetchPersons = () => {
    setLoading(true)
    personService.getAll()
      .then(res => { setPersons((res.persons || []).map(flatten)); setLoading(false) })
      .catch(() => { showMessage('Kunde inte ladda personer', 'error'); setLoading(false) })
  }

  useEffect(() => { fetchPersons() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const ctrl = new AbortController()
    personService.getUsers(ctrl.signal)
      .then(res => setUsers(res.users || []))
      .catch(err => { if (err.name !== 'AbortError') showMessage('Kunde inte ladda Kayenta-personer', 'error') })
    return () => ctrl.abort()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Adapter for the shared Select's isMulti duality: it always reports the single option
  // that was clicked, so the consumer maintains its own array (see NetworkingOverview.js).
  const handleFilterChange = (event) => {
    const { name, value } = event.target !== undefined ? event.target : event
    if (MULTI_FILTER_KEYS.includes(name)) {
      setFilters(prev => {
        const current = prev[name]
        const isSelected = current.some(f => f.value === value)
        return { ...prev, [name]: isSelected ? current.filter(f => f.value !== value) : [...current, { value }] }
      })
    }
  }

  // Filtering happens here, before the data reaches <DataTable> — its own `filterable`
  // prop is a single free-text term over scalar fields and cannot express this.
  const visiblePersons = useMemo(() => applyPersonFilters(persons, filters), [persons, filters])

  const filtersActive = filters.kayentaContact.length > 0 || filters.missingContact
  const missingContactCount = useMemo(() => persons.filter(p => !(p.kayenta_contacts || []).length).length, [persons])

  const deletePersons = (items) => {
    showDeletePersonDialog(items, async (list) => {
      await Promise.all(list.map(p => personService.destroy(p.person_uuid)))
        .then(() => { hideModalDialog(); showMessage(list.length > 1 ? 'Borttagna' : 'Borttagen'); setSelectedItems([]); fetchPersons() })
        .catch(err => showMessage(err.message || 'Kunde inte ta bort', 'error'))
    })
  }

  // Client-side only, same pattern as recruitment's CandidateList — the list
  // already has everything a CSV needs. Leading BOM keeps Excel from mangling
  // å/ä/ö (a plain UTF-8 CSV without it is misread as Latin-1).
  const exportSelected = (items) => {
    const headers = ['Namn', 'Företag', 'Titel', 'E-post', 'Kayenta-kontakt', 'Relationer']
    const escape = (value) => {
      const s = value == null ? '' : String(value)
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const rows = items.map(p => [
      p.person_name,
      p.company_name,
      p.person_title,
      p.person_email,
      p.kayenta_contact_names,
      p.pills.map(pill => pill.label).join('; '),
    ].map(escape).join(','))

    const csv = '﻿' + [headers.join(','), ...rows].join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `personer_${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="wide-margin">
      <div className="network-filter-bar">
        <Select
          className="network-filter-select network-filter-select-narrow"
          name="kayentaContact"
          value={filters.kayentaContact}
          showMultiLabel
          isMulti
          placeholder="Kayenta-kontakt"
          options={formatSelectOptions({ options: users, optionValue: 'user_id', optionLabel: 'name', tooltip: 'email' })}
          onChange={handleFilterChange}
        />
        <Button
          value={`Saknar Kayenta-kontakt (${missingContactCount})`}
          className={'button network-filter-toggle' + (filters.missingContact ? ' network-filter-toggle-active' : '')}
          onClick={() => setFilters(prev => ({ ...prev, missingContact: !prev.missingContact }))}
        />
        {filtersActive &&
          <Button value="Rensa filter" className="button network-filter-clear" onClick={() => setFilters(EMPTY_FILTERS)} />
        }
      </div>
      <DataTable
        data={visiblePersons}
        idColumn="person_uuid"
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        loading={loading}
        filterable
        filterObjects={['person_name', 'person_email', 'company_name', 'person_title', 'kayenta_contact_names']}
        columns={[
          { id: 'person_uuid', name: 'Visa', type: 'button', label: 'Visa', data: { data: 'person_uuid' },
            onClick: (uuid) => navigate(`/${appName}/persons/${uuid}`) },
          { id: 'person_name', name: 'Namn', className: 'bold' },
          { id: 'company_name', name: 'Företag' },
          { id: 'person_title', name: 'Titel' },
          { id: 'person_email', name: 'E-post', email: 'person_email' },
          { id: 'kayenta_contact_names', name: 'Kayenta-kontakt' },
          { id: 'pills', name: 'Relationer', type: 'pills' },
          { id: 'is_internal', name: 'Intern/Extern', type: 'pill', value: 'is_internal', values: INTERNAL_EXTERNAL_VALUES },
        ]}
        buttons={[
          { label: '+ Lägg till', action: 'add', mainButton: true, onClick: () => showBulkImportDialog(() => fetchPersons()) },
          { label: 'Redigera', action: 'edit', hideInToolbar: true, onClick: (items) => showPersonDialog(items[0], () => fetchPersons()) },
          { customButton: selectedItems.length > 0
              ? <BulkActionsMenu items={selectedItems} onExport={exportSelected} onDelete={deletePersons} />
              : null },
          { label: 'Ta bort', action: 'delete', hideInToolbar: true, onClick: (items) => deletePersons(items || selectedItems) },
        ]}
      />
    </div>
  )
}

export default PersonList
